import { Suspense, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { lazyWithRetry } from '@/lib/lazy-with-retry';
import { isPendingUpdate } from '@/lib/sw-update';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { cn, Dialog, DialogContent, DialogTitle } from '@scio3/ui';
import { TopNavigation } from './TopNavigation';
import { PromotionalBanner } from './PromotionalBanner';
import { EngagePlanBanner } from '@/features/engage/components/EngagePlanBanner';
import { useLanguage } from '@/hooks/useLanguage';
import { useMdmPrefetch } from '@/hooks/useMdmPrefetch';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useMatomoPageTracking } from '@/lib/matomo';
import { analyticsOnboardingStarted } from '@/lib/analytics-service';
import { signOut, auth } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { STORAGE_KEY } from '@/lib/i18n';
import {
  isInIframeContext,
  startParentListener,
  notifyNavigation,
} from '@/lib/iframe-child-bridge';
import { Scio1Frame } from '@/components/iframe/Scio1Frame';
import { CanvasAuthBridge } from '@/components/iframe/CanvasAuthBridge';
import { GetStartedChecklist } from '@/features/get-started';
import { OptiBotFAB, OptiBotPanel } from '@/features/optibot';
import { useOptibotStore } from '@/features/optibot/stores/optibot-store';
import { ReferralWidgetMount } from '@/features/referral/ReferralWidgetMount';
import { InvitationAcceptanceDialog } from '@/features/auth/components/InvitationAcceptanceDialog';
import { ShareLinkDialog } from '@/features/share-links/components/ShareLinkDialog';
import { CreationLimitDialog } from '@/features/assets/components/dialogs/CreationLimitDialog';
import { useCreationLimitStore } from '@/features/assets/stores/creation-limit-store';
import { InactiveAccountDialog } from './InactiveAccountDialog';
import { BillingDialogs } from '@/features/billing/dialogs/BillingDialogs';
import { ForcePasswordChangeDialog } from './ForcePasswordChangeDialog';
import { useShouldShowOnboarding } from '@/features/survey/hooks/useShouldShowOnboarding';
import { OnboardingBackdropToolbar } from '@/features/survey/components/OnboardingBackdropToolbar';
import { SurveyLanguageMenu } from '@/features/survey/components/SurveyLanguageMenu';
import { PersistentCanvasFrame } from '@/features/templates/components/PersistentCanvasFrame';
import Scio3WelcomeFlow from '@/features/welcome/components/Scio3WelcomeFlow';

import { clearPersistedPaths } from '@/hooks/usePersistedPath';
import { clearPersistedCache } from '@scio3/graphql-client';

// Lazy-loaded: SmallAppEditDialog pulls the entire asset editing pipeline
// (Uppy, Transloadit, SmallApp microservice, SelectAssetModal ~500+ kB).
// It only renders when a user edits a SmallApp, so lazy-load is safe.
const SmallAppEditDialog = lazyWithRetry(() =>
  import('@/features/assets/components/dialogs/SmallAppEditDialog').then((m) => ({
    default: m.SmallAppEditDialog,
  })),
);

// Lazy-loaded: Split-screen zones editor. Heavier (Apollo queries +
// SelectAssetModal). Phase 4 will lazily load fabric.js inside this
// component, so this keeps the main bundle slim. Templates picker is
// now inline inside this dialog (Option A — single-dialog flow).
const SplitScreenZonesDialog = lazyWithRetry(() =>
  import('@/features/assets/components/dialogs/SplitScreenZonesDialog').then((m) => ({
    default: m.SplitScreenZonesDialog,
  })),
);

// Lazy-loaded: Video-wall template picker (tabs by 2/3/4 screens).
// Sibling of SplitScreenTemplatesDialog — same hot-path lazy pattern.
// Triggered from anywhere via useVideoWallDialogStore.
const VideoWallTemplatesDialog = lazyWithRetry(() =>
  import('@/features/assets/components/dialogs/VideoWallTemplatesDialog').then((m) => ({
    default: m.VideoWallTemplatesDialog,
  })),
);

// Lazy-loaded: Video-wall zones editor. Same lazy fabric loading as
// SplitScreenZonesDialog (the canvas is shared).
const VideoWallZonesDialog = lazyWithRetry(() =>
  import('@/features/assets/components/dialogs/VideoWallZonesDialog').then((m) => ({
    default: m.VideoWallZonesDialog,
  })),
);

// Lazy-loaded: DesignerEditorDialog wraps the Designer iframe in a modal dialog.
// Used when creating a Designer asset from within SelectAssetModal.
const DesignerEditorDialog = lazyWithRetry(() =>
  import('@/features/designer/components/DesignerEditorDialog').then((m) => ({
    default: m.DesignerEditorDialog,
  })),
);

// Lazy-loaded: ModerationDialog opens for social-feed SmallApps (Facebook, Slack, Twitter,
// Instagram, LinkedIn, etc.) so users can hide/show/delete posts before they hit the screens.
const ModerationDialog = lazyWithRetry(() =>
  import('@/features/smallapp/moderation').then((m) => ({
    default: m.ModerationDialog,
  })),
);

// Lazy-loaded: AnimationPreviewDialog opens from the manifest field-action handler
// `previewAnimation` (e.g. Live TV with Ads → Animation select → Preview button).
// Mirrors scio1's previewAnimation modal.
const AnimationPreviewDialog = lazyWithRetry(() =>
  import('@/features/smallapp/components/AnimationPreviewDialog').then((m) => ({
    default: m.AnimationPreviewDialog,
  })),
);

// Lazy-loaded: the onboarding survey pulls react-hook-form (~109 kB). Only shown
// once for new account owners after signup.
const OnboardingSurvey = lazyWithRetry(() =>
  import('@/features/survey/components/OnboardingForm').then((m) => ({
    default: m.OnboardingForm,
  })),
);

const useNativeTemplates = import.meta.env.VITE_USE_NATIVE_TEMPLATES === 'true';

const AUTH_ROUTES = [
  '/signIn',
  '/signUp',
  '/password',
  '/verify',
  '/join',
  '/sso',
  '/auth/sso-login',
  '/sign-email-link',
];

// The app renders at a fixed 1280px logical viewport (desktop layout scaled
// to fit on mobile) — this matches the default in index.html. Auth pages opt
// into the real device width so login/signup are usable on phones. AppShell is
// the single authority for this: auth components (AuthLayout) mutate the shared
// meta tag without restoring it, so AppShell must reset it on every transition
// back into the app, otherwise the device-width override leaks and the whole
// app renders in condensed/responsive mode on mobile (#1416).
const DESKTOP_VIEWPORT = 'width=1280';
const MOBILE_VIEWPORT = 'width=device-width, initial-scale=1, maximum-scale=1';

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isAuthPage = AUTH_ROUTES.some((r) => location.pathname.startsWith(r));
  // Pages that use full-width layout (no horizontal padding on page-wrapper)
  const fullWidthPrefixes = [
    '/app/s/',
    '/app/screenManagement',
    '/app/assetManagement',
    '/app/playlistManagement',
    '/app/scheduleManagement',
    '/app/templateManagement',
    '/app/designManagement',
    '/app/engageManagement',
    '/app/optidev',
    '/app/playbackReport',
    '/app/ai-analytics',
    '/app/mdm',
  ];
  const isFullWidthPage = fullWidthPrefixes.some((p) => location.pathname.startsWith(p));
  // Reactive: true only after confirmed handshake with SCIO 1.0 parent
  const isEmbedded = useAuthStore((s) => s.isEmbedded);
  const token = useAuthStore((s) => s.token);
  const loading = useAuthStore((s) => s.loading);
  const me = useAuthStore((s) => s.me);
  // Post-login white-label flag (same source as PreferencesPage/ApiKeysPage). White-label
  // accounts must not see OptiSigns onboarding/promo surfaces (checklist, welcome flow,
  // promotional banner) — see claude-dev .../12-white-label-show-hide-checklist.md.
  const isWhiteLabel = !!me?.brandingData?.isWhiteLabel;
  // OptiBot is OptiSigns-branded — show it only when OptiSigns branding is NOT removed.
  const removeOptiSignsBranding = !!me?.brandingData?.removeOptiSignsBranding;
  // Global per-app creation-limit dialog — one mount, driven by the store. Both
  // the App Selector pre-check and the save-error echo paths push into it.
  const company = useAuthStore((s) => s.company);
  const creationLimitNotice = useCreationLimitStore((s) => s.notice);
  const closeCreationLimit = useCreationLimitStore((s) => s.close);
  const isOptiBotOpen = useOptibotStore((s) => s.isOpen);
  const isAccountInactive = useAuthStore((s) => s.isAccountInactive);
  const needsPasswordChange = useAuthStore((s) => s.needsPasswordChange);
  const inviteUser = useAuthStore((s) => s.inviteUser);
  const pendingOnboarding = useAuthStore((s) => s.pendingOnboarding);
  const showOnboarding = useShouldShowOnboarding();
  // While the onboarding survey modal is up, the backdrop keeps the real top nav
  // and trial/promo banner (so it still reads as the app), but the cluttered
  // page content is replaced with a blank shell and the competing floating UI
  // (welcome popup, get-started checklist, OptiBot) is suppressed. The modal
  // card then floats over a calm-but-recognizable backdrop. Everything returns
  // automatically once the survey is completed or skipped (showOnboarding → false).
  const surveyCleanShell = showOnboarding;

  // Fire the onboarding-reached funnel marker exactly once, when the survey
  // surface first appears post-signup.
  const onboardingStartedFiredRef = useRef(false);
  useEffect(() => {
    if (showOnboarding && !onboardingStartedFiredRef.current) {
      onboardingStartedFiredRef.current = true;
      analyticsOnboardingStarted();
    }
  }, [showOnboarding]);

  // Dev-only override: `?welcome` in the URL forces the welcome popup to show,
  // bypassing localStorage. Disabled in production builds so end users can't
  // trigger the popup by adding a query param.
  const forceWelcome = useMemo(
    () => import.meta.env.DEV && new URLSearchParams(location.search).has('welcome'),
    [location.search],
  );

  // Sync i18next language with user profile on auth load
  useLanguage();
  // Set document.title based on current route (e.g. "OptiSigns – Screens Management")
  usePageTitle();
  // Track page views in Matomo on every route change (production only)
  useMatomoPageTracking();
  // Idle-time prefetch of the MDM Devices list — warms the Apollo cache so
  // first click on /app/mdm renders instantly. Companion to the chunk preload
  // in `lib/prefetch-routes.ts`. No-op for accounts without `company.feature.mdm`.
  useMdmPrefetch();

  // Reload on route change when a new version is pending.
  // prevPathRef starts as current path so initial mount doesn't trigger.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;

    if (!isPendingUpdate()) return;

    console.warn('[sw-update] Route changed with pending update, reloading');
    window.location.href = location.pathname + location.search;
  }, [location.pathname, location.search]);

  // When in iframe context: listen for parent commands (navigation, language, team, logout).
  // Uses static isInIframeContext so listener is registered early (before handshake completes).
  useEffect(() => {
    if (!isInIframeContext) return;

    const cleanup = startParentListener({
      onRouterRedirect: (url) => {
        // Parent sends a path like "/app/screenManagement" or a state name
        const path = url.startsWith('/') ? url : `/app/${url}`;
        navigate(path);
      },
      onChangeTeam: (teamId) => {
        // Clear team-scoped persisted data before switching teams
        clearPersistedPaths();
        clearPersistedCache();
        useTeamStore.getState().setActiveTeam(teamId);
        // Reload page to clear in-memory Apollo cache and reset all stores
        // (mirrors TeamSwitcher behavior for consistent team isolation)
        window.location.reload();
      },
      onSyncLanguage: (language) => {
        if (language !== i18n.language) {
          i18n.changeLanguage(language);
          localStorage.setItem(STORAGE_KEY, language);
        }
      },
      onLogout: () => {
        signOut(auth);
        navigate('/signIn');
      },
      onReload: () => {
        window.location.reload();
      },
    });

    return cleanup;
  }, [navigate, i18n]);

  // When embedded: notify parent of route changes
  useEffect(() => {
    if (!isEmbedded) return;
    notifyNavigation(location.pathname + location.search);
  }, [isEmbedded, location.pathname, location.search]);

  // Own the viewport meta reactively: device-width on auth pages, fixed 1280px
  // everywhere else. Reset before paint (useLayoutEffect) so navigating from an
  // auth page back into the app doesn't leave the app stuck in mobile/responsive
  // mode (#1416).
  useLayoutEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    meta?.setAttribute('content', isAuthPage ? MOBILE_VIEWPORT : DESKTOP_VIEWPORT);
    if (!isAuthPage) {
      // Entering the app from an auth/onboarding page (rendered at device-width and
      // often scrolled) leaves iOS Safari with a non-zero window scroll, which pushes
      // the sticky TopNavigation above the fold until a manual scroll/zoom/reload.
      // Reset to the top now and again next frame (after iOS finishes re-scaling the
      // viewport, which can re-introduce the offset).
      window.scrollTo(0, 0);
      requestAnimationFrame(() => window.scrollTo(0, 0));
    }
  }, [isAuthPage]);

  if (isAuthPage) {
    // Redirect authenticated users away from auth pages.
    // Require `me` (not just token) — token alone doesn't mean the user is fully authenticated.
    // Prevents flashy redirect when login fails with INVALID_TEAM (token is briefly set by
    // onAuthStateChanged before forceLogout clears it).
    // Skip redirect when pendingOnboarding is true — SignupPage is rendering
    // the onboarding form in-place and should not be navigated away from.
    // Exception: /verify must always render — applyActionCode() needs to run regardless of auth state.
    // /password/reset also needs to render for authenticated users who click reset links.
    // /signIn/optidev (the OptiDev publish popup) must also render for authenticated users:
    // it shows the "Continue as <account>" choice and posts the Firebase token back to the
    // OptiDev opener. Redirecting an authenticated session to the app would abandon the
    // handoff and the popup would never return a token (same reason /verify is excepted).
    const isActionPage =
      location.pathname.startsWith('/verify') ||
      location.pathname.startsWith('/password/reset') ||
      location.pathname.startsWith('/signIn/optidev');
    if (!loading && token && me && !isEmbedded && !pendingOnboarding && !isActionPage) {
      return <Navigate to="/app/screenManagement" replace />;
    }
    return <Outlet />;
  }

  // Gate on full authentication before rendering any app chrome (TopNavigation, sidebar).
  // Mirrors the `isAuthPage` redirect condition above: require `me` not just `token`,
  // because token is set by onAuthStateChanged but me is only set after ME_QUERY succeeds.
  // This prevents any app chrome from flashing for unauthenticated users.
  // - loading: still resolving — show spinner with bg-background (avoids black bar flash)
  // - !me && !isInIframeContext: auth settled but not authenticated — redirect to signIn
  //   (iframe waits for parent custom-token handshake, so we skip the redirect there)
  if (loading || !me) {
    if (!me && !loading && !isInIframeContext) {
      // After explicit logout, don't pass `from` state — prevents cross-user route leakage
      const wasExplicitLogout = sessionStorage.getItem('scio3_explicit_logout');
      return (
        <Navigate to="/signIn" state={wasExplicitLogout ? undefined : { from: location }} replace />
      );
    }
    return (
      <div className="bg-background flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Account payment lapsed — blocking dialog, no app access.
  // Skip when embedded (scio1 parent handles it).
  // InactiveAccountDialog lazy-mounts the scio1 modal adapter iframe only when the user
  // clicks Update — this prevents scio1's own inactive modal from appearing simultaneously.
  if (isAccountInactive && !isEmbedded) {
    return <InactiveAccountDialog />;
  }

  // Admin flagged user to change password on next sign-in — block until done.
  // Skip when embedded (scio1 parent handles it via createPasswordModal).
  if (needsPasswordChange && !isEmbedded) {
    return <ForcePasswordChangeDialog />;
  }

  return (
    <div id="wrapper" className="flex h-dvh flex-col overflow-hidden bg-background">
      {!isInIframeContext && <PromotionalBanner />}
      {/* Engage upgrade banner — rendered full-width here (not inside EngagePage)
          so it spans above both the page content and the OptiBot panel, matching
          PromotionalBanner. Self-hides when the plan already includes Engage.
          The global PromotionalBanner is suppressed on this route
          (usePromotionalBanner: isEngagePage), so the two never stack. */}
      {!isInIframeContext && location.pathname.startsWith('/app/engageManagement') && (
        <EngagePlanBanner />
      )}
      {!isInIframeContext && <TopNavigation locked={surveyCleanShell} />}
      <div className="relative flex min-h-0 flex-1">
        <div
          id="page-wrapper"
          className={cn(
            'relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden [&>*]:min-h-0 [&>*]:flex-1 [&>*]:overflow-hidden',
            !isFullWidthPage && (isOptiBotOpen ? 'pl-3 md:pl-6' : 'px-3 md:px-6'),
          )}
        >
          {surveyCleanShell ? (
            // Modal survey backdrop — a static facsimile of the Screens page
            // (calm, uncluttered) instead of the real page. The survey Dialog
            // renders over this further down.
            <OnboardingBackdropToolbar />
          ) : (
            <>
              <Outlet />
              {useNativeTemplates && <PersistentCanvasFrame />}
            </>
          )}
        </div>
        {import.meta.env.VITE_ENABLE_OPTIBOT === 'true' &&
          !isInIframeContext &&
          !removeOptiSignsBranding &&
          !surveyCleanShell && <OptiBotPanel />}
      </div>
      {/* New account owner onboarding (modal mode) — survey pops up over the
          full portal. Non-dismissible: no close button, and outside-click /
          Escape are blocked so the survey is completed or skipped via its own
          buttons (which fire onComplete → showOnboarding flips false). */}
      {surveyCleanShell && (
        <Dialog open>
          <DialogContent
            hideCloseButton
            // Nudge down ~half the trial banner's height (h-12) so the popup reads
            // as centered in the app area below the banner, not the full viewport.
            className="top-[calc(50%+24px)] max-w-4xl gap-0 overflow-hidden border-0 p-0"
            // Lighter than the default bg-black/60 so the clean-shell backdrop
            // (nav, banner, faux Screens page) shows through behind the survey.
            overlayClassName="bg-black/30"
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            // Don't auto-focus the first focusable child on open (the language
            // menu trigger), which leaves it sitting in a focus-ring state the
            // moment the survey appears. Let the user Tab/click in instead.
            onOpenAutoFocus={(e) => e.preventDefault()}
            data-testid="onboarding-modal"
          >
            <DialogTitle className="sr-only">{t('Welcome to OptiSigns')}</DialogTitle>
            {/* Language picker (real feature, same function as the profile dropdown). */}
            <div className="absolute right-3 top-5 z-10">
              <SurveyLanguageMenu side="bottom" align="end" />
            </div>
            {/* Fixed height so the popup never resizes between steps — the
                survey fills it (h-full) and scrolls internally if a step is tall. */}
            <div className="h-[620px] max-h-[85vh] overflow-hidden">
              <Suspense>
                <OnboardingSurvey modal skipHearAbout />
              </Suspense>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* Mount modal adapter on all pages so openSubscriptionPlan works everywhere,
          including scio1-iframe settings pages (account-members, billing, credentials, etc.) */}
      <Scio1Frame mode="modal" />
      {import.meta.env.VITE_USE_NATIVE_BILLING === 'true' && <BillingDialogs />}
      <Suspense>
        <SmallAppEditDialog />
      </Suspense>
      <Suspense>
        <SplitScreenZonesDialog />
      </Suspense>
      <Suspense>
        <VideoWallTemplatesDialog />
      </Suspense>
      <Suspense>
        <VideoWallZonesDialog />
      </Suspense>
      <Suspense>
        <DesignerEditorDialog />
      </Suspense>
      <Suspense>
        <ModerationDialog />
      </Suspense>
      <Suspense>
        <AnimationPreviewDialog />
      </Suspense>
      <CanvasAuthBridge />
      {!isInIframeContext && !isWhiteLabel && !surveyCleanShell && <GetStartedChecklist />}
      {!isInIframeContext && !isWhiteLabel && !surveyCleanShell && (
        <Scio3WelcomeFlow forceShow={forceWelcome} />
      )}
      {inviteUser && <InvitationAcceptanceDialog />}
      <CreationLimitDialog
        notice={creationLimitNotice}
        isWhiteLabel={!!company?.brandingData?.isWhiteLabel}
        onClose={closeCreationLimit}
      />
      {import.meta.env.VITE_ENABLE_OPTIBOT === 'true' &&
        !isInIframeContext &&
        !removeOptiSignsBranding &&
        !surveyCleanShell && <OptiBotFAB />}
      {/* Referral pill — self-gated (iframe + VITE_ENABLE_REFERRAL_ENGINE) and
          collision-shifts above the OptiBot FAB when the bot panel is open. */}
      <ReferralWidgetMount />
      <ShareLinkDialog />
    </div>
  );
}
